/**
 * The vocabulary of the product feature catalog (PREC-4329, usage analytics 2/10).
 *
 * A catalog entry is a feature a user would name ("Inbox", "Smart list", "HubSpot"), not one entry
 * per event: what the user did with it is a {@link ProductEventAction}, and where they did it is a
 * {@link ProductFeatureSurface}. The entries live in `product-features.ts`.
 */

/**
 * The areas a usage dashboard groups features by. The first nine are the ticket's; the rest exist
 * because a sidebar entry or a measured feature fits none of them.
 */
export const PRODUCT_FEATURE_AREAS = [
	"Lists",
	"Outreach",
	"Inbox",
	"Enrichment",
	"Scoring",
	"CRM",
	"Settings",
	"Extension",
	"MCP",
	"Signals",
	"Contacts",
	"Analytics",
	"Knowledge Base",
	"Integrations",
	"Onboarding",
	"Support",
	"Admin",
	"App",
] as const;

export type ProductFeatureArea = (typeof PRODUCT_FEATURE_AREAS)[number];

/**
 * Where a feature can be used: the web app, the Chrome extension, the MCP server, or the API alone
 * (the public API, and work the server does on its own, such as a CRM sync or a campaign send).
 */
export const PRODUCT_FEATURE_SURFACES = ["web", "extension", "mcp", "api"] as const;

export type ProductFeatureSurface = (typeof PRODUCT_FEATURE_SURFACES)[number];

/**
 * What a usage event says the user did with a feature: a closed set, never free text. `active` is
 * the server's daily `app` event (PREC-4335) and nothing else; a browser never reports it.
 */
export const PRODUCT_EVENT_ACTIONS = [
	"view",
	"create",
	"run",
	"export",
	"connect",
	"configure",
	"send",
	"active",
] as const;

export type ProductEventAction = (typeof PRODUCT_EVENT_ACTIONS)[number];

/**
 * One catalog entry.
 *
 * - `key` is stable and kebab-case. It is stored with every event, so it is never derived from the
 *   label and never renamed.
 * - `label` is English (the web UI is English-only).
 * - `featureCode` names the `FeaturesCodes` value that gates the feature, as a string literal: the
 *   enum is duplicated in the API and the web app, and this package can import neither, so a spec
 *   on each side checks that every code exists there. Rollout switches (ENFORCE_CREDITS,
 *   SHOW_CREDITS, IRL_COUNTRIES_SUM, SELF_REGISTER) are not features and never appear here.
 */
export interface ProductFeatureDefinition {
	readonly key: string;
	readonly label: string;
	readonly area: ProductFeatureArea;
	readonly surfaces: readonly ProductFeatureSurface[];
	readonly featureCode?: string;
}
